import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import cn from "classnames";

import { ScoreCategory, ScoreDistributionEntry } from "@/modules/assessments/types/insights.interface";
import { CompositeIcon } from "@/shared/components/assessment/CompositeIcon/CompositeIcon";
import { pluralize } from "@/shared/utils/pluralize";
import { BUCKET_COLORS } from "@/shared/components/charts/utils/chartUtils";

import style from "./score-distribution-chart.module.css";

interface ScoreDistributionChartProps {
  icon: string | null;
  title: string;
  distribution: ScoreDistributionEntry[];
  className?: string;
}

interface BarDatum {
  label: string;
  percentage: number;
  count: number;
  color: string;
  category: ScoreCategory;
}

function buildChartData(distribution: ScoreDistributionEntry[]): BarDatum[] {
  return distribution.map((entry) => ({
    label: String(entry.score),
    percentage: entry.percentage,
    count: entry.count,
    color: BUCKET_COLORS[entry.category],
    category: entry.category,
  }));
}

function CustomTooltipContent({ active, payload }: { active?: boolean; payload?: { payload: BarDatum }[] }) {
  if (!active || !payload?.length) return null;
  const { count, percentage } = payload[0].payload;
  return (
    <div className={style.tooltip}>
      <span className={style.tooltipPercentage}>{percentage}%</span>
      <span className={style.tooltipCount}>
        {count} {pluralize("candidate", count)}
      </span>
    </div>
  );
}

const Y_AXIS_TICKS = [0, 25, 50, 75, 100];

export function ScoreDistributionChart({ icon, title, distribution, className }: ScoreDistributionChartProps) {
  const data = buildChartData(distribution);

  return (
    <div className={cn(style.chartCard, className)}>
      <div className={style.chartTitleRow}>
        <CompositeIcon icon={icon} className={style.chartTitleIcon} />
        <span className={style.chartTitle}>{title}</span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
          <YAxis
            domain={[0, 100]}
            ticks={Y_AXIS_TICKS}
            tickFormatter={(v: number) => `${v}%`}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11 }}
            width={40}
          />
          <Tooltip content={<CustomTooltipContent />} cursor={false} />
          <Bar dataKey="percentage" radius={[4, 4, 0, 0]} barSize={60}>
            {data.map((entry) => (
              <Cell key={entry.label} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
